<script>
  import { scaleLinear, scaleSqrt } from 'd3-scale'
  import { extent, range } from 'd3-array'
  import { format } from 'd3-format'
  import Guidelines from './Guidelines.svelte'

  export let allData
  export let filteredData
  export let selectedIndicator
  export let selectedFilter = []
  export let selectedCountry = ''
  export let activeDropdown
  export let isMobile

  const margin = { top: 20, right: 10, bottom: 65, left: 75 }

  let width = 600
  let height = 600

  $: mainWidth = width - margin.right - margin.left
  $: mainHeight = height - margin.top - margin.bottom

  let maxXVal =
    Math.ceil(Math.max(...allData.map((d) => d.vaccine_index)) / 10) * 10
  let maxYVal =
    Math.ceil(Math.max(...allData.map((d) => d.medical_aid_index)) / 10) * 10

  $: xTicks = range(0, maxXVal + 20, 20)

  $: yTicks = range(0, maxYVal + 20, 20)

  $: xScale = scaleLinear()
    .domain([0, Math.max(...xTicks)])
    .range([0, mainWidth])

  $: yScale = scaleLinear()
    .domain([0, Math.max(...yTicks)])
    .range([mainHeight, 0])

  $: rScale = scaleSqrt()
    .domain(extent(allData, (d) => d.gdp))
    .range([4, 25])

  const formatter = format('.1f')

  // Helper f(n) for formatDecimalPlaces
  function formatAmount(value) {
    if (window.innerWidth < 768) {
      return value.replace(/G/, 'B').slice(-1)[0]
    }

    if (value.includes('T')) {
      return ' trillion'
    } else if (value.includes('G')) {
      return ' billion'
    } else {
      return ' million'
    }
  }

  /* formats values up to two decimal places while maintaining 1-3 digits left of the first comma (eg 500.00B or 1.00T) */
  function formatDecimalPlaces(value) {
    let amt = formatAmount(format('.5s')(value))
    let numOne = format('$.5s')(value).split('.')[0]
    let numTwo = format('.5s')(value).split('.')[1].slice(0, 1)
    return numOne + '.' + numTwo + amt
  }

  $: toggleClass = (d, name) => {
    if (selectedCountry === '') {
      return name
    } else if (selectedCountry === d.country) {
      return `${name} is-active`
    } else if (selectedCountry !== d.country) {
      return `${name} is-faded`
    }
  }

  $: activeCircle = allData.find((d) => {
    return d.country === selectedCountry
  })
</script>

<figure
  class="interactive__charts"
  bind:clientWidth="{width}"
  bind:clientHeight="{height}"
>
  <svg>
    <!-- x axis -->
    <g
      class="axis axis--x"
      transform="translate({margin.left}, {height - margin.bottom})"
    >
      <line
        class="axis-line"
        x1="0"
        y2="0"
        y1="0"
        x2="{width - margin.right - margin.left}"
      >
      </line>

      {#each xTicks as tick}
        <g class="tick" transform="translate({xScale(tick)}, 0)">
          <line y1="0" y2="7" fill="currentColor" stroke="black"></line>
          <text
            class="interactive__subheading"
            dy="25"
            x="0"
            text-anchor="middle"
            fill="currentColor"
          >
            {tick}
          </text>
        </g>
      {/each}
      <text class="interactive__subheading subheading-x" fill="currentColor">
        Vaccine Diplomacy Index
      </text>
    </g>

    <g class="axis axis--y" transform="translate({margin.left}, {margin.top})">
      <line
        class="axis-line"
        x1="0"
        x2="0"
        y1="0"
        y2="{height - margin.top - margin.bottom}"
      >
      </line>
      {#each yTicks as tick}
        <g class="tick" transform="translate(0, {yScale(tick)})">
          <line x1="0" x2="-7" fill="currentColor"></line>
          <text
            class="interactive__subheading"
            dx="-25"
            y="7"
            text-anchor="start"
            fill="currentColor"
          >
            {tick}
          </text>
        </g>
      {/each}
      <text class="interactive__subheading subheading-y" fill="currentColor">
        Medical Diplomacy Index
      </text>
    </g>

    <!-- data -->
    <g class="chart__index" transform="translate({margin.left}, {margin.top})">
      {#each filteredData as data}
        <Guidelines
          country="{data}"
          xScale="{xScale}"
          yScale="{yScale}"
          height="{height}"
          margin="{margin}"
          toggleClass="{toggleClass}"
          formatter="{formatter}"
        />
        <circle
          cx="{xScale(data.vaccine_index)}"
          cy="{yScale(data.medical_aid_index)}"
          r="{rScale(data.gdp)}"
          data-color="{selectedIndicator === 'region'
            ? `${data.region}`
            : `${data.income_level}`}"
          class="{toggleClass(data, 'circle')}"
          id="{data.country}"
          data-iso="{data.iso}"
          on:mouseenter="{() => {
            selectedCountry = data.country
            activeDropdown = false
          }}"
          on:mouseleave="{() => {
            selectedCountry = ''
          }}"
          on:mouseup="{() => {
            selectedCountry = data.country
            activeDropdown = false
          }}"
        >
        </circle>
      {/each}
      {#if activeCircle && activeDropdown}
        <circle
          cx="{xScale(activeCircle.vaccine_index)}"
          cy="{yScale(activeCircle.medical_aid_index)}"
          r="{rScale(activeCircle.gdp)}"
          data-color="{selectedIndicator === 'region'
            ? `${activeCircle.region}`
            : `${activeCircle.income_level}`}"
          class="{toggleClass(activeCircle, 'circle')}"
          id="{activeCircle.country}"
          data-iso="{activeCircle.iso}"
          on:mouseenter="{() => {
            selectedCountry = activeCircle.country
            activeDropdown = false
          }}"
          on:mouseleave="{() => (selectedCountry = '')}"
          on:mouseup="{() => {
            selectedCountry = activeCircle.country
            activeDropdown = false
          }}"
        >
        </circle>
      {/if}
    </g>
  </svg>
</figure>
<div class="interactive__tooltip">
  <h2 class="tooltip__heading">
    {activeCircle ? activeCircle.country : 'Country:'}
  </h2>
  <ul class="country-values" role="list">
    <li>
      <div>Vaccine Diplomacy Index:</div>
      {activeCircle ? formatter(activeCircle.vaccine_index) : '--'}
    </li>
    <li>
      <div>Medical Diplomacy Index:</div>
      {activeCircle ? formatter(activeCircle.medical_aid_index) : '--'}
    </li>
    <li>
      <div>GDP:</div>
      {activeCircle ? formatDecimalPlaces(activeCircle.gdp) : '--'}
    </li>
  </ul>
</div>

<style type="text/scss" global>
  @import '../scss/custom/_chart.scss';
  @import '../scss/components/_tooltips.scss';
</style>
